import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { useCart } from '../hooks/useCart'
import { api } from '../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  ArrowLeftIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  StarIcon,
} from '@heroicons/react/24/outline'
import { CATEGORY_VALUE_TO_SLUG, CATEGORY_VALUE_TO_LABEL } from '../constants/product-categories'

interface ProductDetail {
  id: string
  name: string
  slug: string
  category: string
  description: string
  price: number | string
  compareAtPrice?: number | string
  images: string[]
  attributes?: {
    material?: string
    color?: string
    size?: string
  }
  vendor: {
    id: string
    companyName: string
    city?: string
    state?: string
    isVerified: boolean
  }
  inStock: boolean
  quantity: number
  views: number
  totalSales: number
  createdAt: string
}

const SouvenirProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { addItem, replaceCartWithItem, vendorMismatch, vendorName } = useCart()
  const [product, setProduct] = useState<ProductDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [addingToCart, setAddingToCart] = useState(false)

  useEffect(() => {
    if (slug) {
      fetchProduct(slug)
    }
  }, [slug])

  const fetchProduct = async (productSlug: string) => {
    try {
      setLoading(true)
      const response = await api.get(`/souvenir-products/slug/${productSlug}`)
      setProduct(response.data)
    } catch (err: any) {
      console.error('Error fetching product:', err)
      setError(err.response?.data?.message || 'Failed to fetch product')
    } finally {
      setLoading(false)
    }
  }

  const handleAddToCart = async () => {
    if (!product) return

    // Cart requires an account (checkout needs wallet payment)
    if (!user) {
      toast.error(t('cart.loginRequired'))
      navigate('/login')
      return
    }

    setAddingToCart(true)
    const cartPayload = {
      productId: product.id,
      name: product.name,
      price: typeof product.price === 'number' ? product.price : parseFloat(product.price),
      image: product.images?.[0],
      vendorId: product.vendor.id,
      vendorName: product.vendor.companyName,
      category: product.category,
      slug: product.slug,
    }

    if (vendorMismatch(product.vendor.id)) {
      // Single-vendor cart: replacing clears items from another vendor
      if (window.confirm(
        t('cart.replaceConfirm', { vendor: vendorName || '' }),
      )) {
        replaceCartWithItem(cartPayload, quantity)
        toast.success(t('cart.addedToCart', { quantity, name: product.name }))
      }
    } else {
      addItem(cartPayload, quantity)
      toast.success(t('cart.addedToCart', { quantity, name: product.name }))
    }
    setAddingToCart(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          Product not found
        </div>
      </div>
    )
  }

  const price = typeof product.price === 'number' ? product.price : parseFloat(product.price)
  const compareAtPrice = product.compareAtPrice != null ? Number(product.compareAtPrice) : null
  const discount = compareAtPrice && compareAtPrice > price
    ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
    : 0

  return (
    <div className="max-w-7xl mx-auto">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Link
          to={`/products/${CATEGORY_VALUE_TO_SLUG[product.category] || 'other'}`}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to {CATEGORY_VALUE_TO_LABEL[product.category] || 'Products'}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Images */}
        <div className="space-y-4">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {product.images && product.images.length > 0 ? (
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-96 object-cover"
              />
            ) : (
              <div className="w-full h-96 bg-gray-200 flex items-center justify-center">
                <ShoppingBagIcon className="h-24 w-24 text-gray-400" />
              </div>
            )}
          </div>

          {product.images && product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-4">
              {product.images.slice(1, 5).map((image, index) => (
                <div key={index} className="bg-white shadow rounded-lg overflow-hidden">
                  <img
                    src={image}
                    alt={`${product.name} ${index + 2}`}
                    className="w-full h-24 object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className="space-y-6">
          {/* Vendor */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sold by</p>
                <p className="text-lg font-semibold text-gray-900">
                  {product.vendor.companyName}
                </p>
                <p className="text-sm text-gray-500">
                  {product.vendor.city && `${product.vendor.city}, `}
                  {product.vendor.state}
                </p>
              </div>
              {product.vendor.isVerified && (
                <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full">
                  <StarIcon className="h-4 w-4" />
                  <span className="text-sm font-medium">Verified Vendor</span>
                </div>
              )}
            </div>
          </div>

          {/* Title & Price */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {CATEGORY_VALUE_TO_LABEL[product.category] || product.category}
                </span>
                <h1 className="text-3xl font-bold text-gray-900 mt-3">{product.name}</h1>
                <p className="text-gray-600 mt-2">{product.description}</p>
              </div>
            </div>

            <div className="mt-6">
              {discount > 0 ? (
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-gray-900">
                    RM{price.toFixed(2)}
                  </span>
                  <span className="text-xl text-gray-500 line-through">
                    RM{compareAtPrice?.toFixed(2)}
                  </span>
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                    {discount}% OFF
                  </span>
                </div>
              ) : (
                <span className="text-3xl font-bold text-gray-900">
                  RM{price.toFixed(2)}
                </span>
              )}
            </div>

            {/* Stats */}
            <div className="mt-6 flex items-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <ShoppingBagIcon className="h-4 w-4" />
                <span>{product.totalSales} sold</span>
              </div>
              <div className="flex items-center gap-1">
                <ShoppingCartIcon className="h-4 w-4" />
                <span>{product.views} views</span>
              </div>
            </div>
          </div>

          {/* Attributes */}
          {product.attributes && (product.attributes.material || product.attributes.color || product.attributes.size) && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Product Details</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {product.attributes.material && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Material</dt>
                    <dd className="mt-1 text-sm text-gray-900">{product.attributes.material}</dd>
                  </div>
                )}
                {product.attributes.color && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Color</dt>
                    <dd className="mt-1 text-sm text-gray-900">{product.attributes.color}</dd>
                  </div>
                )}
                {product.attributes.size && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Size</dt>
                    <dd className="mt-1 text-sm text-gray-900">{product.attributes.size}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Quantity & Add to Cart */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100"
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  max={product.quantity}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center border-0 focus:ring-0"
                />
                <button
                  onClick={() => setQuantity(Math.min(product.quantity, quantity + 1))}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100"
                  disabled={quantity >= product.quantity}
                >
                  +
                </button>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={addingToCart || !product.inStock}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition ${
                  !product.inStock
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                <ShoppingCartIcon className="h-5 w-5" />
                {addingToCart ? 'Adding...' : !product.inStock ? 'Out of Stock' : 'Add to Cart'}
              </button>
            </div>

            {!product.inStock && (
              <p className="mt-4 text-sm text-red-600 text-center">
                This product is currently out of stock
              </p>
            )}

            <p className="mt-4 text-sm text-gray-500 text-center">
              {product.quantity} items available
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SouvenirProductDetailPage
